import { convertToDateTime } from "../utils";
import { z } from "zod/v3";
import { FormValues, SessionSchema } from "./schema";

type SessionData = {
  startTime: string;
  endTime: string;
};

export const validateSessionTimes = (data: SessionData): boolean => {
  const start = convertToDateTime(data.startTime);
  const end = convertToDateTime(data.endTime);

  if (!start || !end) {
    return false;
  }

  return end > start;
};

export const validateOnsiteFields = (data: FormValues, ctx: z.RefinementCtx): void => {
  if (!data.address1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Street Address is required",
      path: ["address1"]
    });
  }
  if (!data.city) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "City is required",
      path: ["city"]
    });
  }
  if (!data.state) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "State is required",
      path: ["state"]
    });
  }
  if (!data.zipcode) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Zipcode is required",
      path: ["zipcode"]
    });
  }
};

export const validateFacility = (data: FormValues, ctx: z.RefinementCtx) => {
  const isOnsiteLocation = data.locationType === "onsite";
  const isFacilityInfoMissing = !data.facility || !data.facilityId;

  if (isOnsiteLocation && isFacilityInfoMissing) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Facility is required",
      path: ["facility"]
    });
  }
};

export const validateRegistrationOptions = (data: FormValues, ctx: z.RefinementCtx) => {
  const registrationOpenDate = data.registrationOpenDate.startDate;
  const registrationCloseDate = data.registrationCloseDate.startDate;

  if (registrationCloseDate < registrationOpenDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Registration close date must be after registration open date",
      path: ["registrationCloseDate"]
    });
  }

  const hasMaxAttendance = data.sessions.some(({ maxAttendance }) => !!maxAttendance);
  const waitlistType = !!data.waitlistType;

  if (hasMaxAttendance && !waitlistType) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Waitlist Type is required",
      path: ["waitlistType"]
    });
  }
};

export const validateProduct = (data: { id: string; variantPrice: number; collection: string }) => {
  const hasNoSelection = !data.id;
  const hasCompleteSelection = !!(data.id && data.variantPrice && data.collection);

  return hasNoSelection || hasCompleteSelection;
};

export const validatePATNationalFee = (
  products: { id: string; variantId: string; variantPrice: number }[] | undefined,
  ctx: z.RefinementCtx,
  isEditMode?: boolean
): void => {
  const nationalFee = products?.[0];
  // Edits load the saved fee before the national product and variant resolve, and keep it when neither can.
  if (isEditMode && (nationalFee?.variantPrice ?? 0) > 0) return;

  const isResolved = !!nationalFee?.id && !!nationalFee.variantId;
  if (isResolved) return;

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message: "National PAT fee is required",
    path: ["products", 0, "variantId"]
  });
};

export const validatePATRegistrationCloseDate = (
  sessions: z.infer<typeof SessionSchema>[],
  registrationCloseDate: string,
  ctx: z.RefinementCtx
): void => {
  if (!sessions[0]?.date?.startDate || !registrationCloseDate) return;

  const sessionDate = new Date(sessions[0].date.startDate);
  const maxAllowedCloseDate = new Date(sessionDate);
  maxAllowedCloseDate.setDate(maxAllowedCloseDate.getDate() - 7);

  const closeDate = new Date(registrationCloseDate);

  if (closeDate > maxAllowedCloseDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "PAT registration close date must be at least 7 days before the event",
      path: ["registrationCloseDate"]
    });
  }
};

export const validatePATSessionDate = (
  sessions: z.infer<typeof SessionSchema>[],
  ctx: z.RefinementCtx,
  isEditMode?: boolean,
  originalRegistrationCloseDate?: any
) => {
  const getMinDate = (): Date => {
    if (isEditMode) {
      const now = new Date();
      const registrationClose = new Date(originalRegistrationCloseDate.startDate);

      if (now > registrationClose) {
        return now;
      }
    }

    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date;
  };

  const sessionStartDate = new Date(sessions[0].date.startDate);
  const minDate = getMinDate();

  if (sessionStartDate < minDate) {
    const message = isEditMode
      ? "The event cannot be moved to an earlier date"
      : "PAT events must start at least one week from today";
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message,
      path: ["sessions.0.date.startDate"]
    });
  }
};
